import { useAuth } from "./AuthProvider";
import {
  BU_HEAD_ROLE_ID,
  DEPT_ADMIN_ROLE_ID,
  GROUP_ADMIN_ROLE_ID,
  INITIATOR_ROLE_ID,
  EDITOR_ROLE_ID,
  SUPER_ADMIN_ROLE_ID,
} from "../constants/roleIds";

export const useCan = (module: string, action: string): boolean => {
  const { hasPermission } = useAuth();

  return hasPermission(module, action);
};

/** The backend's "Group Admin" role id (Emeis.Shared.Util.RightsUtil.GroupLeader)
 * — this system's term for "Org Admin". Mirrors how RightsUtil.IsAdmin on the
 * backend defines admin-ness as SuperAdmin OR GroupLeader (not the separate,
 * plain "Admin" role, which the backend does not treat as admin-level).
 *
 * Matched by id, not by name: display names are editable, both in the Roles
 * screen and by the seeder, and this check gates admin access across the whole
 * app — a rename must never be able to silently revoke it. The other role
 * checks below already match by id for the same reason. */

/** True for a Super Admin (cross-organisation) specifically — used to gate
 * anything that should be visible/assignable only to a Super Admin, e.g.
 * the Super Admin role option itself. */
export const useIsSuperAdmin = (): boolean => {
  const { user } = useAuth();
  return !!user?.isAppAdmin;
};

/** True for a Super Admin or this organisation's Group Admin ("Org Admin")
 * — the backend's own definition of admin-ness (see RightsUtil.IsAdmin),
 * kept in sync here so the UI hides/disables the same things the backend
 * would reject. Not the same as the generic per-module useCan() permission
 * check below — this is a role-identity gate on top of it. */
export const useIsOrgOrSuperAdmin = (): boolean => {
  const { user } = useAuth();
  return !!user?.isAppAdmin || !!user?.roleIds?.includes(GROUP_ADMIN_ROLE_ID);
};

/** The seeded "Dept-Admin" role id (Emeis.Shared.Util.RightsUtil.DeptAdmin).
 * Matched by id rather than name because role names are editable, and because
 * this is the same value the backend enforces the scope with. */

/** The seeded "BU Head" role id (Emeis.Shared.Util.RightsUtil.BuHead).
 * Matched by id for the same reason as Dept-Admin above. */

/**
 * True for a Department Admin who holds no wider role.
 *
 * An Org/Super Admin who also carries Dept-Admin keeps org-wide visibility —
 * mirrors RightsUtil.IsRestrictedToOwnDepartment on the backend, which is what
 * actually enforces the scope. This hook only decides what the UI shows; it is
 * never the boundary.
 */
export const useIsDeptAdmin = (): boolean => {
  const { user } = useAuth();

  if (user?.isAppAdmin) return false;
  if (user?.roleIds?.includes(GROUP_ADMIN_ROLE_ID)) return false;

  // A BU Head who also carries Dept-Admin sees their whole business unit, so
  // the department panel would understate what they have. Mirrors
  // RightsUtil.IsRestrictedToOwnDepartment, which defers the same way.
  if (user?.roleIds?.includes(BU_HEAD_ROLE_ID)) return false;

  return !!user?.roleIds?.includes(DEPT_ADMIN_ROLE_ID);
};

/**
 * True for a BU Head who holds no wider role.
 *
 * One level above a Department Admin: every department under their business
 * unit. Mirrors RightsUtil.IsRestrictedToOwnBusinessUnit on the backend, which
 * is what actually enforces the scope — this only decides what the UI shows.
 */
export const useIsBuHead = (): boolean => {
  const { user } = useAuth();

  if (user?.isAppAdmin) return false;
  if (user?.roleIds?.includes(GROUP_ADMIN_ROLE_ID)) return false;

  return !!user?.roleIds?.includes(BU_HEAD_ROLE_ID);
};

/**
 * How far this user may reach when choosing a contract's department.
 *
 * Mirrors ContractDepartmentEditGuard.GetChoiceScopeAsync on the backend,
 * which is what actually enforces it. The UI has to agree with it: the server
 * silently files a contract into the caller's own department when the choice
 * isn't allowed, so a picker offered to someone outside the rule would hand
 * back a different department than the one they picked, with no error.
 */
export type ContractDepartmentChoiceScope = "none" | "ownBusinessUnit" | "any";

export const useContractDepartmentChoiceScope =
  (): ContractDepartmentChoiceScope => {
    const { user } = useAuth();
    const roleIds = user?.roleIds ?? [];

    if (
      user?.isAppAdmin ||
      roleIds.includes(SUPER_ADMIN_ROLE_ID) ||
      roleIds.includes(GROUP_ADMIN_ROLE_ID) ||
      roleIds.includes(DEPT_ADMIN_ROLE_ID) ||
      roleIds.includes(INITIATOR_ROLE_ID) ||
      roleIds.includes(EDITOR_ROLE_ID)
    ) {
      return "any";
    }

    // A BU Head reaches across their own unit's departments, no further.
    if (roleIds.includes(BU_HEAD_ROLE_ID)) return "ownBusinessUnit";

    return "none";
  };

/**
 * Who may move a contract between departments at all. Kept as its own hook
 * because the detail screens only need the yes/no, not the reach.
 */
export const useCanEditContractDepartment = (): boolean =>
  useContractDepartmentChoiceScope() !== "none";

export const useModuleVisible = (module: string): boolean => {
  const { isModuleVisible } = useAuth();

  return isModuleVisible(module);
};
